import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { sampleEdgePoints } from '../pointCloudUtils';
import { COLD_PRIMARY, COLD_DIM, COLD_FROST } from '../twinColors';
const OUTER_W = 8;
const OUTER_H = 5;
const OUTER_D = 6;
const INNER_W = 7.2;
const INNER_H = 4.2;
const INNER_D = 5.2;
const FROST_COUNT = 120;

/**
 * ColdStorage - Point cloud double-walled box with frost particles
 * and thermometer bar. Enhanced frost particle count for the
 * point cloud aesthetic.
 */
export default function ColdStorage({ state, position, onClick }) {
    const pointsRef = useRef();
    const fillRef = useRef();

    const compressorOn = state?.compressor_on ?? false;
    const temperatureF = state?.temperature_f ?? 35;

    // Outer wall particles (dispose intermediate geometries)
    const outerPoints = useMemo(() => {
        const box = new THREE.BoxGeometry(OUTER_W, OUTER_H, OUTER_D);
        const edges = new THREE.EdgesGeometry(box);
        const pts = sampleEdgePoints(edges, 500, 0.06);
        box.dispose(); edges.dispose();
        return pts;
    }, []);

    // Inner wall particles (sparser)
    const innerPoints = useMemo(() => {
        const box = new THREE.BoxGeometry(INNER_W, INNER_H, INNER_D);
        const edges = new THREE.EdgesGeometry(box);
        const pts = sampleEdgePoints(edges, 250, 0.05);
        box.dispose(); edges.dispose();
        return pts;
    }, []);

    // Frost particles inside
    const frostPositions = useMemo(() => {
        const positions = new Float32Array(FROST_COUNT * 3);
        for (let i = 0; i < FROST_COUNT; i++) {
            positions[i * 3] = (Math.random() - 0.5) * (INNER_W - 0.5);
            positions[i * 3 + 1] = Math.random() * INNER_H + 0.3;
            positions[i * 3 + 2] = (Math.random() - 0.5) * (INNER_D - 0.5);
        }
        return positions;
    }, []);

    // Thermometer particles
    const thermoPoints = useMemo(() => {
        const box = new THREE.BoxGeometry(0.25, 3, 0.05);
        const edges = new THREE.EdgesGeometry(box);
        const pts = sampleEdgePoints(edges, 30, 0.02);
        box.dispose(); edges.dispose();
        return pts;
    }, []);

    useFrame(() => {
        // Frost particle drift
        if (pointsRef.current) {
            const posArr = pointsRef.current.geometry.attributes.position.array;
            const driftSpeed = compressorOn ? 0.03 : 0.008;

            for (let i = 0; i < FROST_COUNT; i++) {
                posArr[i * 3 + 1] -= driftSpeed;
                if (posArr[i * 3 + 1] < 0.3) {
                    posArr[i * 3 + 1] = 4 + Math.random();
                    posArr[i * 3] = (Math.random() - 0.5) * (INNER_W - 0.5);
                    posArr[i * 3 + 2] = (Math.random() - 0.5) * (INNER_D - 0.5);
                }
            }
            pointsRef.current.geometry.attributes.position.needsUpdate = true;
            pointsRef.current.material.opacity = compressorOn ? 0.5 : 0.2;
        }

        // Thermometer fill height
        if (fillRef.current) {
            const ratio = Math.min(1, Math.max(0, (45 - temperatureF) / 45));
            const fillH = ratio * 2.8;
            fillRef.current.scale.y = Math.max(0.01, fillH);
            fillRef.current.position.y = 1.1 + fillH / 2;
        }
    });

    return (
        <group position={position} onClick={onClick}>
            {/* Outer wall particles */}
            <points position={[0, 2.5, 0]}>
                <bufferGeometry>
                    <bufferAttribute
                        attach="attributes-position"
                        count={outerPoints.length / 3}
                        array={outerPoints}
                        itemSize={3}
                    />
                </bufferGeometry>
                <pointsMaterial
                    color={COLD_PRIMARY}
                    size={0.1}
                    transparent
                    opacity={0.4}
                    depthWrite={false}
                    sizeAttenuation
                    blending={THREE.AdditiveBlending}
                />
            </points>

            {/* Inner wall particles */}
            <points position={[0, 2.5, 0]}>
                <bufferGeometry>
                    <bufferAttribute
                        attach="attributes-position"
                        count={innerPoints.length / 3}
                        array={innerPoints}
                        itemSize={3}
                    />
                </bufferGeometry>
                <pointsMaterial
                    color={COLD_DIM}
                    size={0.08}
                    transparent
                    opacity={0.2}
                    depthWrite={false}
                    sizeAttenuation
                    blending={THREE.AdditiveBlending}
                />
            </points>

            {/* Frost particles */}
            <points ref={pointsRef}>
                <bufferGeometry>
                    <bufferAttribute
                        attach="attributes-position"
                        count={FROST_COUNT}
                        array={frostPositions}
                        itemSize={3}
                    />
                </bufferGeometry>
                <pointsMaterial
                    color={COLD_FROST}
                    size={0.15}
                    transparent
                    opacity={0.2}
                    depthWrite={false}
                    sizeAttenuation
                    blending={THREE.AdditiveBlending}
                />
            </points>

            {/* Thermometer outline particles */}
            <points position={[4.05, 2.5, 0]}>
                <bufferGeometry>
                    <bufferAttribute
                        attach="attributes-position"
                        count={thermoPoints.length / 3}
                        array={thermoPoints}
                        itemSize={3}
                    />
                </bufferGeometry>
                <pointsMaterial
                    color={COLD_PRIMARY}
                    size={0.06}
                    transparent
                    opacity={0.4}
                    depthWrite={false}
                    sizeAttenuation
                    blending={THREE.AdditiveBlending}
                />
            </points>

            {/* Fill bar */}
            <mesh ref={fillRef} position={[4.05, 1.1, 0]}>
                <planeGeometry args={[0.2, 1]} />
                <meshBasicMaterial
                    color={COLD_PRIMARY}
                    transparent
                    opacity={0.4}
                    depthWrite={false}
                    blending={THREE.AdditiveBlending}
                />
            </mesh>
        </group>
    );
}
